import { useEffect, useMemo } from 'react';
import { Card } from '../../components/base/Card';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useLocation, useParams } from 'react-router-dom';
import { Alert } from '../../components/base/Alert';
import { FaultApplianceDetails } from '../../components/Fault/ApplianceDetails';
import { FaultDetails } from '../../components/Fault/FaultDetails';
import { FaultCauses } from '../../components/Fault/FaultCauses';
import { FaultActions } from '../../components/Fault/FaultActions';
import { FaultResources } from '../../components/Fault/FaultResources';
import useFault from '../../data/fault/useFault';
import { SectionLoading } from '../../components/shared/SectionLoading';
import useUserNotifications from '../../data/user/useUserNotifications';
import { FaultHeatmap } from '../../components/Fault/FaultHeatmap';

export default function FaultDetailsPage() {
  const { setPageTitle } = usePageTitle();
  const { faultDefinitionId } = useParams();
  const location = useLocation();
  const applianceId = location.state?.applianceId;
  const { fault: faultDef } = useFault(faultDefinitionId ?? '');
  const { userNotifications: faultNotifications } = useUserNotifications(
    true,
    applianceId
  );

  const faultHistory = useMemo(
    () =>
      faultNotifications.data
        ? faultNotifications.data
            .filter((fn) => fn.faultDefinitionId === faultDefinitionId)
            .sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
            )
        : [],
    [faultNotifications.data, faultDefinitionId]
  );

  const fault = useMemo(
    () => (faultDef.data ? faultDef.data[0] : null),
    [faultDef.data]
  );

  useEffect(() => {
    setPageTitle('Fault Details');
  }, [setPageTitle]);

  if (!faultDefinitionId || !applianceId) {
    return (
      <Alert
        severity="error"
        title="Missing Required Information"
        message="Missing fault definition ID or appliance ID in URL parameters. Please try again, if the issue persists please contact support."
      />
    );
  }

  if (faultDef.isLoading) {
    return <SectionLoading />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
      <FaultDetails
        faultDefinitionId={faultDefinitionId}
        lastOccurred={new Date(faultHistory[0]?.timestamp)}
      />
      <FaultApplianceDetails applianceId={applianceId} />
      <Card className="lg:col-span-3 space-y-4">
        <span className="text-lg font-medium">Causes</span>
        <FaultCauses faultCauseIds={fault?.causes || []} />
      </Card>
      <Card className="lg:col-span-3 space-y-4">
        <span className="text-lg font-medium">Actions</span>
        <FaultActions
          faultActionIds={fault?.actions.map((fa) => fa.faultActionId) || []}
        />
      </Card>
      <Card className="lg:col-span-6 space-y-4">
        <span className="text-lg font-medium">History</span>
        <FaultHeatmap faults={faultHistory} />
      </Card>
      <Card className="lg:col-span-6 space-y-4">
        <span className="text-lg font-medium">Resources</span>
        <FaultResources resources={fault?.resources || []} />
      </Card>
    </div>
  );
}
